import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Sparkle, TrendUp, ArrowUp, Rocket, FileText, Target, VideoCamera } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import AppLayout from '@/components/AppLayout';
import PageTransition from '@/components/PageTransition';
import OnboardingChecklist from '@/components/OnboardingChecklist';
import { SkeletonDashboard } from '@/components/ui/skeleton-loader';
import { useTheme } from '@/contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const { isDark } = useTheme();

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dashboard/stats`, {
        withCredentials: true,
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
    fetchStats();
    
    // Listen for pull-to-refresh events
    const handleRefresh = () => {
      fetchStats();
    };
    window.addEventListener('pulltorefresh', handleRefresh);
    return () => window.removeEventListener('pulltorefresh', handleRefresh);
  }, [searchParams, fetchStats]);

  const checkPaymentStatus = async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        toast.error('Délai de vérification dépassé');
        return;
      }

      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/payments/status/${sessionId}`,
          { withCredentials: true }
        );

        if (response.data.payment_status === 'paid') {
          toast.success('Paiement réussi ! Bienvenue en Premium');
          return;
        }

        attempts++;
        setTimeout(poll, pollInterval);
      } catch (error) {
        console.error('Error checking payment:', error);
      }
    };

    poll();
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="p-8">
          <SkeletonDashboard />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageTransition>
        <div className="p-6 md:p-8 lg:p-10 space-y-8 relative z-10" data-testid="dashboard">
          {/* Header Premium */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mb-2"
          >
            {/* Badge */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-5 ${
              isDark 
                ? 'bg-white/[0.04] border border-white/[0.08]' 
                : 'bg-violet-50 border border-violet-100'
            }`}>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400"></span>
              </span>
              <span className={`text-sm font-medium ${isDark ? 'text-white/70' : 'text-violet-700'}`}>
                {t('dashboard.subtitle', 'Votre assistant IA est actif')}
              </span>
            </div>
            
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Bienvenue sur
              <span className="block gradient-text mt-1">votre tableau de bord</span>
            </h1>
            <p className={`text-lg md:text-xl max-w-2xl ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              Gérez votre acquisition client et suivez vos performances en temps réel
            </p>
          </motion.div>

          {/* Onboarding Checklist */}
          <OnboardingChecklist />

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Visibility Score */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className={`p-6 col-span-2 rounded-2xl ${
                isDark 
                  ? 'glass-card' 
                  : 'bg-white border border-gray-100 shadow-sm'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white/80' : 'text-gray-900'}`}>
                  {t('dashboard.visibilityScore')}
                </h2>
                <span className="text-xs text-emerald-500 bg-emerald-500/10 px-3 py-1.5 rounded-full flex items-center gap-1.5 border border-emerald-500/20">
                  <ArrowUp size={14} weight="bold" />
                  +{stats?.score_evolution || 0}%
                </span>
              </div>
              <div className="flex items-end gap-4">
                <div className="text-6xl md:text-7xl font-bold gradient-text">
                  {stats?.visibility_score || 0}
                </div>
                <div className={`text-lg mb-3 font-light ${isDark ? 'text-white/30' : 'text-gray-400'}`}>/100</div>
              </div>
              <div className={`mt-6 h-2.5 rounded-full overflow-hidden ${isDark ? 'bg-white/[0.06]' : 'bg-gray-100'}`}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${stats?.visibility_score || 0}%` }}
                  transition={{ duration: 1, ease: 'easeOut' }}
                  className="h-full bg-gradient-to-r from-violet-500 to-pink-500 rounded-full"
                ></motion.div>
              </div>
            </motion.div>

            {/* Content Count */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className={`p-6 rounded-2xl ${
                isDark 
                  ? 'glass-card' 
                  : 'bg-white border border-gray-100 shadow-sm'
              }`}
            >
              <h2 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white/80' : 'text-gray-900'}`}>
                Contenu créé
              </h2>
              <div className="text-5xl font-bold gradient-text">{stats?.content_count || 0}</div>
              <p className={`text-sm mt-3 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                Posts générés ce mois
              </p>
            </motion.div>
          </div>

          {/* AI Message */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className={`p-6 rounded-2xl ${
              isDark 
                ? 'glass-card' 
                : 'bg-white border border-gray-100 shadow-sm'
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isDark 
                  ? 'bg-gradient-to-br from-violet-500/20 to-pink-500/20' 
                  : 'bg-violet-100'
              }`}>
                <Sparkle size={20} weight="duotone" className="text-violet-500" />
              </div>
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white/80' : 'text-gray-900'}`}>
                Message de l'IA
              </h2>
            </div>
            <p className={`leading-relaxed ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
              {stats?.ai_message}
            </p>
          </motion.div>

          {/* CTA Principal - Générer des clients */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.35 }}
          >
            <Link to="/seo-generator">
              <div className="relative overflow-hidden rounded-3xl p-10 md:p-12 cursor-pointer hover:scale-[1.01] transition-all duration-300 group">
                <div className="absolute inset-0 bg-gradient-to-br from-violet-600 via-purple-600 to-pink-600"></div>
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMDUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-50"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                <div className="relative z-10 text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm mb-6">
                    <Rocket size={18} weight="fill" className="text-white" />
                    <span className="text-sm text-white/90 font-medium">Recommandé pour vous</span>
                  </div>
                  <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                    Attirez des clients
                    <span className="block">grâce à Google</span>
                  </h3>
                  <p className="text-white/75 text-lg md:text-xl mb-8 max-w-xl mx-auto">
                    Créez 30 pages SEO optimisées en quelques clics. Trafic garanti.
                  </p>
                  <button 
                    className="bg-white text-violet-600 px-10 py-4 rounded-2xl font-bold text-lg hover:bg-white/95 transition-all shadow-xl group-hover:shadow-2xl group-hover:scale-105 duration-300"
                    data-testid="generate-clients-cta"
                  >
                    Générer mes premiers clients
                  </button>
                  <p className="text-white/50 text-sm mt-4">Configuration en 2 minutes • Sans compétences techniques</p>
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Actions principales */}
          <div>
            <h2 className={`text-2xl md:text-3xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Vos outils
              <span className="gradient-text ml-2">d'acquisition</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <ActionCard icon={Sparkle} title="Créer du contenu" description="Posts optimisés en 1 clic" path="/generate" delay={0.4} isDark={isDark} />
              <ActionCard icon={FileText} title="Pages SEO" description="Vos pages d'acquisition" path="/seo-pages" delay={0.45} isDark={isDark} />
              <ActionCard icon={Target} title="Suivre Google" description="Vos positions en temps réel" path="/ranking" delay={0.5} isDark={isDark} />
              <ActionCard icon={VideoCamera} title="Créer des vidéos" description="Vidéos marketing IA" path="/video" delay={0.55} isDark={isDark} />
            </div>
          </div>

          {/* Recommendations */}
          {stats?.recommendations && stats.recommendations.length > 0 && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className={`p-6 rounded-2xl ${
                isDark 
                  ? 'glass-card' 
                  : 'bg-white border border-gray-100 shadow-sm'
              }`}
            >
              <h2 className={`text-lg font-semibold mb-5 ${isDark ? 'text-white/80' : 'text-gray-900'}`}>
                Recommandations
              </h2>
              <ul className="space-y-4">
                {stats.recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      isDark 
                        ? 'bg-gradient-to-br from-violet-500/20 to-pink-500/20' 
                        : 'bg-violet-100'
                    }`}>
                      <span className="text-xs font-semibold text-violet-500">{i + 1}</span>
                    </div>
                    <span className={`leading-relaxed ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                      {rec}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </div>
      </PageTransition>
    </AppLayout>
  );
}

function ActionCard({ icon: Icon, title, description, path, delay, isDark }) {
  return (
    <Link to={path} className="block">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay }}
        className={`p-6 rounded-2xl transition-all duration-300 group cursor-pointer h-full hover:-translate-y-1 ${
          isDark 
            ? 'glass-card hover:border-violet-500/30' 
            : 'bg-white border border-gray-100 shadow-sm hover:shadow-lg'
        }`}
        data-testid={`action-${title.toLowerCase().replace(/\s/g, '-')}`}
      >
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all ${
          isDark 
            ? 'bg-gradient-to-br from-violet-500/15 to-pink-500/15 group-hover:from-violet-500/25 group-hover:to-pink-500/25' 
            : 'bg-violet-100 group-hover:bg-violet-200'
        }`}>
          <Icon size={24} weight="duotone" className="text-violet-500" />
        </div>
        <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white/90' : 'text-gray-900'}`}>
          {title}
        </h3>
        <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-600'}`}>
          {description}
        </p>
      </motion.div>
    </Link>
  );
}
